import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

const IMG_WHEEL =
  "https://images.unsplash.com/photo-1768370770523-07a42d64209d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHw0fHxtb3RvcnNwb3J0JTIwaGVyaXRhZ2UlMjBkZXRhaWx8ZW58MHx8fHwxNzg4MTE4NjI0fDA&ixlib=rb-4.1.0&q=85";
const IMG_PAN =
  "https://images.unsplash.com/photo-1543068298-386e90d0d86c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxzcG9ydHMlMjBjYXIlMjBjaW5lbWF0aWMlMjBsaWdodGluZ3xlbnwwfHx8fDE3ODgxMTg2MjR8MA&ixlib=rb-4.1.0&q=85";

const chapters = [
  {
    n: "01",
    title: "We publish obsession",
    copy: "MotorLegendsMedia exists for the ones who hear a cold-start V12 and feel it in their ribs. Every volume is researched like a documentary and written like a thriller.",
  },
  {
    n: "02",
    title: "Machines are the characters",
    copy: "We don't write spec sheets. We write biographies — of engines, of chassis, of the people who risked everything behind the wheel when safety was a suggestion.",
  },
  {
    n: "03",
    title: "Heritage, not nostalgia",
    copy: "This isn't a museum. It's fuel. Each e-book is built to be read in one sitting and remembered for years — cinematic, rigorous, unapologetically loud.",
  },
];

export const Manifesto = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY1 = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const imgY2 = useTransform(scrollYProgress, [0, 1], [120, -40]);

  return (
    <section ref={ref} id="manifesto" className="relative py-28 md:py-44 overflow-hidden" data-testid="manifesto-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="text-xs font-semibold tracking-[0.4em] uppercase text-primary mb-16 md:mb-24"
        >
          The Manifesto
        </motion.p>

        <div className="relative">
          <motion.div
            style={{ y: imgY1 }}
            className="hidden lg:block absolute right-[8%] top-[-6rem] w-72 xl:w-80 border border-white/20 spotlight"
          >
            <img src={IMG_WHEEL} alt="Heritage steering wheel detail" className="w-full aspect-[3/4] object-cover" data-testid="manifesto-image-wheel" />
          </motion.div>
          <motion.div
            style={{ y: imgY2 }}
            className="hidden lg:block absolute right-[28%] bottom-[-4rem] w-80 xl:w-96 border border-white/20 spotlight"
          >
            <img src={IMG_PAN} alt="Racing car at speed" className="w-full aspect-[4/3] object-cover" data-testid="manifesto-image-pan" />
          </motion.div>

          <div className="space-y-24 md:space-y-36 lg:max-w-[62%]">
            {chapters.map((c, i) => (
              <motion.div
                key={c.n}
                initial={{ opacity: 0, y: 60 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="relative"
                data-testid={`manifesto-chapter-${c.n}`}
              >
                <span className="font-display text-[7rem] md:text-[10rem] leading-none text-primary/90 select-none">
                  {c.n}
                </span>
                <h3 className="font-editorial italic text-3xl md:text-5xl -mt-6 md:-mt-10 ml-2 md:ml-10 relative z-10">
                  {c.title}
                </h3>
                <p className="mt-6 md:ml-10 max-w-lg text-sm md:text-base leading-relaxed text-foreground/60">
                  {c.copy}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
